import dayjs from 'dayjs';
import { CSSProperties } from 'react';
import { formatLedgerMoney, LedgerRow, RevenueLedgerEntry, sourceLabel } from '../ledger/ledgerRows';

/** A refund or cancellation request awaiting its refund, as the bot returns it (BAR-3033). Dollars, positive. */
export interface RefundRequest {
  id: string;
  medplumPatientId: string | null;
  patientName: string | null;
  requestedAt: string;
  /** Cancels the procedure: a Refund. Otherwise a Customer service fee. */
  cancel: boolean;
  reason: string;
  note: string | null;
  amountRequested: number;
  amountApproved: number | null;
  approvalNote: string | null;
}

export interface RefundWorklists {
  requests: RefundRequest[];
  unlinkedRefunds: RevenueLedgerEntry[];
}

/** The rows' Approve and Link refund buttons. */
export const ROW_BUTTON_STYLE: CSSProperties = {
  fontSize: 12,
  fontWeight: 500,
  lineHeight: '16px',
  padding: '5px 10px',
  borderRadius: 8,
  border: '1px solid rgba(0, 0, 0, 0.10)',
  background: '#fff',
  color: 'black',
  whiteSpace: 'nowrap',
};

/** "Refunded, no request" starts with Phase 1; older refunds predate requests. */
export const REFUND_WORKLIST_SINCE = '2026-10-01T00:00:00.000Z';

export function requestType({ cancel }: Pick<RefundRequest, 'cancel'>): 'Refund' | 'Customer service fee' {
  return cancel ? 'Refund' : 'Customer service fee';
}

/** `Sep 21, 2026`. */
export function formatRequestDate(at: string): string {
  return dayjs(at).format('MMM D, YYYY');
}

/** `Sep 21`. */
export function formatShortDate(at: string): string {
  return dayjs(at).format('MMM D');
}

/** The amount typed, in dollars; 0 when it isn't a number. */
export function parseAmount(value: string): number {
  const amount = Number.parseFloat(value.replace(/[^0-9.]/g, ''));
  return Number.isFinite(amount) ? Math.round(amount * 100) / 100 : 0;
}

const cents = (amount: number): number => Math.round(amount * 100);

export interface LinkCandidate {
  refund: LedgerRow;
  why: string[];
}

/**
 * Link refund's list: every refund with no request, the likeliest first. Same
 * patient and same amount (against the amount requested) weigh 2; a payer name
 * that matches the patient weighs 1, for a refund with no patient yet.
 */
export function linkCandidates(request: RefundRequest, refunds: LedgerRow[]): LinkCandidate[] {
  const family = request.patientName?.split(' ').pop()?.toLowerCase();
  return refunds
    .map((refund) => {
      const why: string[] = [];
      let score = 0;
      if (refund.medplumPatientId && refund.medplumPatientId === request.medplumPatientId) {
        why.push('Same patient');
        score += 2;
      }
      if (cents(-refund.amount) === cents(request.amountRequested)) {
        why.push('Same amount');
        score += 2;
      }
      if (!refund.medplumPatientId && family && refund.payerName?.toLowerCase().includes(family)) {
        why.push('Payer name');
        score += 1;
      }
      return { refund, why, score };
    })
    .sort((a, b) => b.score - a.score)
    .map(({ refund, why }) => ({ refund, why }));
}

export function approvedMessage(request: RefundRequest, name: string, amount: number, linked: boolean): string {
  return `${requestType(request)} of ${formatLedgerMoney(amount)} approved · card added to ${name}'s timeline${
    linked ? ' · refund linked' : ''
  }`;
}

export function issuedMessage(request: RefundRequest, name: string, refund: LedgerRow): string {
  return `${requestType(request)} issued · ${formatLedgerMoney(-refund.amount)} via ${sourceLabel(
    refund.source
  )} · card added to ${name}'s timeline`;
}
